import { createSlice } from '@reduxjs/toolkit';
import type { MetricData } from 'utils/assets';

export interface tireProfileState {
  limits: any[];
  /** Axle-wise limits from /tire-profile/limits/axle-wise
   *  Shape: Array<{ make, model, patternName, totalAxles, positions: AxleWisePosition[] }>
   */
  axleWiseLimits: any[];
  tireData: Record<string, unknown>;
  isLoadingFLeet: boolean;
  isLoadingVehicle: boolean;
  isPollingVehicle: boolean;
  fleetData: {
    pressure: MetricData;
    temperature: MetricData;
    assetTires: any[];
  };
  // Battery status per deviceId -> per tireCode (e.g., 0x17) -> { isBatteryLow, voltageValue, timeStamp }
  batteryData: Record<
    string,
    Record<
      string,
      { isBatteryLow: boolean; voltageValue?: number; timeStamp?: number }
    >
  >;
  isLoadingDashboard: boolean;
  dashboard: any;
  tireFitmentData: any[];
  fitmentHistory: any[];
}

const initialState: tireProfileState = {
  limits: [],
  axleWiseLimits: [],
  tireData: {},
  isLoadingFLeet: false,
  isLoadingVehicle: false,
  isPollingVehicle: false,
  fleetData: { pressure: {}, temperature: {}, assetTires: [] },
  batteryData: {},
  isLoadingDashboard: false,
  dashboard: {
    data: {},
    notifications: [],
    tpmsLeakageNotifications: [],
    fuelRate: { fleet: [] },
  },
  tireFitmentData: [],
  fitmentHistory: [],
};

export const tireProfileSlice = createSlice({
  name: 'tireProfile',
  initialState,
  reducers: {
    setLimits(state, { payload }) {
      state.limits = payload;
    },
    setAxleWiseLimits(state, { payload }) {
      state.axleWiseLimits = payload;
    },
    setDeviceTireData(state, { payload }) {
      const existing =
        (state.tireData[payload.device] as Record<string, any>) || {};
      const newData = payload.data || {};

      state.tireData[payload.device] = {
        ...existing,
        ...newData,
        // Preserve notifications, fitment data, and history if newData is empty/missing
        notifications:
          newData.notifications?.length > 0
            ? newData.notifications
            : existing.notifications || [],
        tireFitmentData:
          newData.tireFitmentData?.length > 0
            ? newData.tireFitmentData
            : existing.tireFitmentData || [],
        fitmentHistory:
          newData.fitmentHistory?.length > 0
            ? newData.fitmentHistory
            : existing.fitmentHistory || [],
      };
    },
    setIsLoadingFleet(state, { payload }) {
      state.isLoadingFLeet = payload;
    },
    setIsLoadingVehicle(state, { payload }) {
      state.isLoadingVehicle = payload;
    },
    setIsPollingVehicle(state, { payload }) {
      state.isPollingVehicle = payload;
    },
    setFleetData(state, { payload }) {
      state.fleetData = payload;
    },
    setIsLoadingDashboard(state, { payload }) {
      state.isLoadingDashboard = payload;
    },
    setDashboard(state, { payload }) {
      state.dashboard = payload;
    },
    suspendNotification(state, { payload }) {
      const { notificationId, source = 'notification' } = payload;

      if (source === 'leakage') {
        const index = state.dashboard.tpmsLeakageNotifications.findIndex(
          (item: any) => item.notificationId === notificationId
        );
        if (index !== -1) {
          state.dashboard.tpmsLeakageNotifications[index] = {
            ...state.dashboard.tpmsLeakageNotifications[index],
            isActive: false,
            alertEndTs: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }
      } else {
        const index = state.dashboard.notifications.findIndex(
          (item: any) => item.notificationId === notificationId
        );
        if (index !== -1) {
          state.dashboard.notifications[index] = {
            ...state.dashboard.notifications[index],
            isActive: false,
            alertEndTs: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }
      }
    },
    setDashboardNotifications(state, { payload }) {
      state.dashboard.notifications = payload;
    },
    setDashboardLeakageNotifications(state, { payload }) {
      state.dashboard.tpmsLeakageNotifications = payload;
    },
    setBatteryData(state, { payload }) {
      // payload is Record<deviceId, Record<tireCode, { isBatteryLow, voltageValue, timeStamp }>>
      state.batteryData = { ...state.batteryData, ...payload };
    },
  },
});

export const {
  setLimits,
  setAxleWiseLimits,
  setDeviceTireData,
  setIsLoadingFleet,
  setIsLoadingVehicle,
  setIsPollingVehicle,
  setFleetData,
  setIsLoadingDashboard,
  setDashboard,
  suspendNotification,
  setDashboardNotifications,
  setDashboardLeakageNotifications,
  setBatteryData,
} = tireProfileSlice.actions;

export default tireProfileSlice.reducer;
